import React, { useState, useEffect } from 'react';
import { Calendar, List, LayoutGrid, Plus, AlertCircle, CheckCircle, Clock, FileText, Plane } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { caymanDateKey, formatDateShort, CAYMAN_TZ } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RelocationTracker = () => {
  const [view, setView] = useState('kanban'); // 'calendar', 'list', 'kanban'
  const [relocations, setRelocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    fetchRelocations();
  }, []);

  const fetchRelocations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/relocations`, {
        headers: {
          'Authorization': 'Basic ' + btoa(':Pawsome2026!')
        }
      });
      const data = await response.json();
      setRelocations(data);
      calculateAlerts(data);
    } catch (error) {
      logger.error('Error fetching relocations:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAlerts = (relocationsData) => {
    // Compute "today" midnight in Cayman TZ for a stable day-based diff
    const todayKey = caymanDateKey(new Date());
    const [ty, tm, td] = todayKey.split('-').map(Number);
    const todayMidnightUTC = Date.UTC(ty, tm - 1, td);
    const upcomingAlerts = [];

    relocationsData.forEach(relocation => {
      if (relocation.travel_date) {
        const travelKey = caymanDateKey(relocation.travel_date);
        const [y, m, d] = travelKey.split('-').map(Number);
        const travelMidnightUTC = Date.UTC(y, m - 1, d);
        const daysUntil = Math.round(
          (travelMidnightUTC - todayMidnightUTC) / (1000 * 60 * 60 * 24)
        );

        if (daysUntil <= 30 && daysUntil > 0) {
          upcomingAlerts.push({
            type: 'travel',
            message: `${relocation.pet_name} travels in ${daysUntil} days`,
            priority: daysUntil <= 7 ? 'high' : 'medium',
            relocation: relocation
          });
        }

        // Check if documents are incomplete and travel is soon
        if (daysUntil <= 14 && daysUntil > 0 && !allDocumentsComplete(relocation)) {
          upcomingAlerts.push({
            type: 'documents',
            message: `${relocation.pet_name} has incomplete documents (${daysUntil} days until travel)`,
            priority: 'high',
            relocation: relocation
          });
        }
      }
    });

    setAlerts(upcomingAlerts);
  };

  const allDocumentsComplete = (relocation) => {
    const docs = relocation.documents || {};
    return docs.health_certificate && docs.import_permit && docs.vaccination_records;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-gray-200 text-gray-800',
      'quote_sent': 'bg-gray-200 text-gray-800',
      'booking_confirmed': 'bg-blue-200 text-blue-800',
      'documentation': 'bg-yellow-200 text-yellow-800',
      'documents_required': 'bg-yellow-200 text-yellow-800',
      'documents_submitted': 'bg-yellow-200 text-yellow-800',
      'documents_under_review': 'bg-yellow-200 text-yellow-800',
      'documents_approved': 'bg-green-200 text-green-800',
      'booked': 'bg-blue-200 text-blue-800',
      'travel_arrangements': 'bg-blue-200 text-blue-800',
      'flight_booked': 'bg-blue-200 text-blue-800',
      'in_transit': 'bg-purple-200 text-purple-800',
      'delivered': 'bg-green-200 text-green-800',
      'completed': 'bg-green-200 text-green-800'
    };
    return colors[status] || 'bg-gray-200 text-gray-800';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Loading relocations...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Pet Relocation Tracker</h1>
            <p className="text-gray-600">Manage all import/export schedules</p>
          </div>
          <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2" data-testid="btn-quick-add-relocation">
            <Plus className="w-4 h-4" />
            Quick Add Relocation
          </Button>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6" data-testid="critical-deadlines-alert">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 mb-2" data-testid="critical-deadlines-count">Critical Deadlines ({alerts.length})</h3>
                <ul className="space-y-1">
                  {alerts.map((alert) => (
                    <li key={`${alert.type}-${alert.relocation.id}`} className="text-sm text-amber-800">
                      • {alert.message}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* View Selector */}
        <div className="flex gap-2 border-b border-gray-200" data-testid="view-selector">
          <button
            onClick={() => setView('kanban')}
            data-testid="btn-kanban-view"
            className={`pb-3 px-4 flex items-center gap-2 border-b-2 font-medium transition-colors ${
              view === 'kanban'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
            Kanban Board
          </button>
          <button
            onClick={() => setView('list')}
            data-testid="btn-list-view"
            className={`pb-3 px-4 flex items-center gap-2 border-b-2 font-medium transition-colors ${
              view === 'list'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <List className="w-4 h-4" />
            List View
          </button>
          <button
            onClick={() => setView('calendar')}
            data-testid="btn-calendar-view"
            className={`pb-3 px-4 flex items-center gap-2 border-b-2 font-medium transition-colors ${
              view === 'calendar'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Calendar className="w-4 h-4" />
            Calendar
          </button>
        </div>
      </div>

      {/* Views */}
      {view === 'kanban' && <KanbanView relocations={relocations} onUpdate={fetchRelocations} />}
      {view === 'list' && <ListView relocations={relocations} />}
      {view === 'calendar' && <CalendarView relocations={relocations} />}

      {/* Quick Add Modal */}
      {showAddModal && (
        <QuickAddModal 
          onClose={() => setShowAddModal(false)} 
          onSuccess={() => {
            setShowAddModal(false);
            fetchRelocations();
          }}
        />
      )}
    </div>
  );
};

// Kanban View Component
const KanbanView = ({ relocations, onUpdate }) => {
  const statuses = [
    { key: 'pending', label: 'Pending', color: 'gray' },
    { key: 'quote_sent', label: 'Quote Sent', color: 'gray' },
    { key: 'documentation', label: 'Documentation', color: 'yellow' },
    { key: 'documents_required', label: 'Docs Required', color: 'yellow' },
    { key: 'documents_submitted', label: 'Docs Submitted', color: 'yellow' },
    { key: 'documents_under_review', label: 'Under Review', color: 'yellow' },
    { key: 'booked', label: 'Booked', color: 'blue' },
    { key: 'flight_booked', label: 'Flight Booked', color: 'blue' },
    { key: 'in_transit', label: 'In Transit', color: 'purple' },
    { key: 'completed', label: 'Completed', color: 'green' }
  ];

  const getRelocationsByStatus = (status) => {
    return relocations.filter(r => (r.status || r.current_status) === status);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-6">
      {statuses.map(status => (
        <div key={status.key} className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">{status.label}</h3>
            <span className="text-xs bg-gray-200 px-2 py-1 rounded-full">
              {getRelocationsByStatus(status.key).length}
            </span>
          </div>
          
          <div className="space-y-3">
            {getRelocationsByStatus(status.key).map(relocation => (
              <RelocationCard key={relocation.id} relocation={relocation} onUpdate={onUpdate} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Relocation Card Component
const RelocationCard = ({ relocation, onUpdate }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowDetails(!showDetails)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Plane className="w-4 h-4 text-blue-600" />
            <h4 className="font-bold text-sm">{relocation.pet_name}</h4>
          </div>
        </div>
        
        <div className="text-xs text-gray-600 space-y-1">
          <p>Client: {relocation.client_name || 'N/A'}</p>
          <p className="flex items-center gap-1">
            <span>{relocation.origin}</span>
            <span>→</span>
            <span>{relocation.destination}</span>
          </p>
          {relocation.travel_date && (
            <p className="text-blue-600 font-medium">
              {formatDateShort(relocation.travel_date)}
            </p>
          )}
        </div>

        {showDetails && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <DocumentStatus relocation={relocation} compact />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Document Status Component
const DocumentStatus = ({ relocation, compact = false }) => {
  const docs = relocation.documents || {};
  
  const documentChecklist = [
    { key: 'health_certificate', label: 'Health Certificate' },
    { key: 'import_permit', label: 'Import Permit' },
    { key: 'vaccination_records', label: 'Vaccination Records' },
    { key: 'microchip_cert', label: 'Microchip Certificate' }
  ];

  if (compact) {
    const completed = documentChecklist.filter(d => docs[d.key]).length;
    return (
      <div className="text-xs">
        <p className="font-medium mb-1">Documents: {completed}/{documentChecklist.length}</p>
        {documentChecklist.map(doc => (
          <div key={doc.key} className="flex items-center gap-1">
            {docs[doc.key] ? (
              <CheckCircle className="w-3 h-3 text-green-600" />
            ) : (
              <Clock className="w-3 h-3 text-gray-400" />
            )}
            <span className={docs[doc.key] ? 'text-green-600' : 'text-gray-500'}>
              {doc.label}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {documentChecklist.map(doc => (
        <div key={doc.key} className="flex items-center gap-2">
          {docs[doc.key] ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <Clock className="w-4 h-4 text-gray-400" />
          )}
          <span className="text-sm">{doc.label}</span>
        </div>
      ))}
    </div>
  );
};

// List View Component
const ListView = ({ relocations }) => {
  return (
    <div className="mt-6">
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pet</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Travel Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documents</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {relocations.map(relocation => {
              const docs = relocation.documents || {};
              const completed = Object.values(docs).filter(Boolean).length;
              const total = 4;

              return (
                <tr key={relocation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{relocation.pet_name}</div>
                    <div className="text-sm text-gray-500">{relocation.pet_type}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {relocation.client_name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {relocation.origin} → {relocation.destination}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {relocation.travel_date ? formatDateShort(relocation.travel_date) : 'TBD'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(relocation.status || relocation.current_status)}`}>
                      {(relocation.status || relocation.current_status || 'pending').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ width: `${(completed / total) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-600">{completed}/{total}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const getStatusColor = (status) => {
  const colors = {
    'pending': 'bg-gray-200 text-gray-800',
    'quote_sent': 'bg-gray-200 text-gray-800',
    'booking_confirmed': 'bg-blue-200 text-blue-800',
    'documentation': 'bg-yellow-200 text-yellow-800',
    'documents_required': 'bg-yellow-200 text-yellow-800',
    'documents_submitted': 'bg-yellow-200 text-yellow-800',
    'documents_under_review': 'bg-yellow-200 text-yellow-800',
    'documents_approved': 'bg-green-200 text-green-800',
    'booked': 'bg-blue-200 text-blue-800',
    'travel_arrangements': 'bg-blue-200 text-blue-800',
    'flight_booked': 'bg-blue-200 text-blue-800',
    'in_transit': 'bg-purple-200 text-purple-800',
    'delivered': 'bg-green-200 text-green-800',
    'completed': 'bg-green-200 text-green-800'
  };
  return colors[status] || 'bg-gray-200 text-gray-800';
};

// Calendar View Component (dates rendered in Cayman Islands timezone)
const CalendarView = ({ relocations }) => {
  // currentDate is only used to track which month/year is visible.
  // We keep it in the browser's local TZ but only use its year/month components.
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Build grid cells: each cell is either null (blank) or { year, month, day, key }.
  const getDaysInMonth = () => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    // Leading blanks (align Sunday-first week)
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }

    // Day cells
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const y = String(year);
      const m = String(month + 1).padStart(2, '0');
      const dd = String(d).padStart(2, '0');
      days.push({ year, month, day: d, key: `${y}-${m}-${dd}` });
    }

    return days;
  };

  // A relocation belongs to a calendar cell when its travel_date's
  // Cayman-local YYYY-MM-DD matches the cell's YYYY-MM-DD.
  const getRelocationsForDate = (cell) => {
    if (!cell) return [];
    return relocations.filter(
      r => r.travel_date && caymanDateKey(r.travel_date) === cell.key
    );
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  // "Today" in Cayman timezone (YYYY-MM-DD)
  const todayCaymanKey = caymanDateKey(new Date());

  const days = getDaysInMonth();

  return (
    <div className="mt-6">
      <div className="bg-white rounded-lg shadow p-6">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {monthNames[month]} {year}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Times shown in Cayman Islands ({CAYMAN_TZ})
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={previousMonth} data-testid="calendar-prev-month">Previous</Button>
            <Button variant="outline" onClick={nextMonth} data-testid="calendar-next-month">Next</Button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="text-center font-semibold text-gray-600 py-2">
              {d}
            </div>
          ))}

          {days.map((cell, idx) => {
            const dayRelocations = getRelocationsForDate(cell);
            const isToday = cell && cell.key === todayCaymanKey;

            return (
              <div
                key={cell ? cell.key : `blank-${idx}`}
                className={`min-h-24 border rounded p-2 ${
                  !cell ? 'bg-gray-50' : isToday ? 'bg-blue-50 border-blue-300' : 'bg-white'
                }`}
              >
                {cell && (
                  <>
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      {cell.day}
                    </div>
                    <div className="space-y-1">
                      {dayRelocations.map(relocation => (
                        <div
                          key={relocation.id}
                          className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded truncate"
                          title={`${relocation.pet_name} - ${relocation.origin} to ${relocation.destination}`}
                        >
                          <Plane className="w-3 h-3 inline mr-1" />
                          {relocation.pet_name}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Quick Add Modal Component
const QuickAddModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    pet_name: '',
    pet_type: 'dog',
    client_name: '',
    origin: 'Cayman Islands',
    destination: '',
    travel_date: '',
    status: 'pending'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/relocations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(':Pawsome2026!')
        },
        body: JSON.stringify({
          ...formData,
          documents: {
            health_certificate: false,
            import_permit: false,
            vaccination_records: false,
            microchip_cert: false
          }
        })
      });

      if (response.ok) {
        onSuccess();
      }
    } catch (error) {
      logger.error('Error adding relocation:', error);
      alert('Failed to add relocation');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold mb-4">Quick Add Relocation</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Pet Name *</label>
            <input
              type="text"
              required
              value={formData.pet_name}
              onChange={(e) => setFormData({...formData, pet_name: e.target.value})}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Pet Type</label>
            <select
              value={formData.pet_type}
              onChange={(e) => setFormData({...formData, pet_type: e.target.value})}
              className="w-full border rounded px-3 py-2"
            >
              <option value="dog">Dog</option>
              <option value="cat">Cat</option>
              <option value="bird">Bird</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Client Name *</label>
            <input
              type="text"
              required
              value={formData.client_name}
              onChange={(e) => setFormData({...formData, client_name: e.target.value})}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Origin</label>
              <input
                type="text"
                value={formData.origin}
                onChange={(e) => setFormData({...formData, origin: e.target.value})}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Destination *</label>
              <input
                type="text"
                required
                value={formData.destination}
                onChange={(e) => setFormData({...formData, destination: e.target.value})}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Travel Date</label>
            <input
              type="date"
              value={formData.travel_date}
              onChange={(e) => setFormData({...formData, travel_date: e.target.value})}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" className="flex-1">Add Relocation</Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RelocationTracker;
